/*
 * Bracci Hero (<bracci-hero>): troca de slides do banner principal.
 *
 * - Esmaecer, deslizar ou esmaecer com zoom (classe bhr--fade | bhr--slide | bhr--zoom).
 * - Setas, indicadores (traços com barra de tempo, bolinhas ou números), teclado
 *   (setas esquerda/direita) e arrasto com o dedo ou o mouse.
 * - Troca sozinha com pausa ao passar o mouse, ao focar com o teclado, com a aba
 *   escondida, com o banner fora da tela e pelo botão de pausa. A barra de tempo
 *   continua de onde parou.
 * - Slides escondidos no desktop ou no celular saem da rotação naquele tamanho.
 * - Editor de temas: ao selecionar um slide, o banner vai até ele e pausa.
 */
(() => {
  if (customElements.get('bracci-hero')) return;

  const DESKTOP = window.matchMedia('(min-width: 750px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class BracciHero extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-bhr-track]');
      this.viewport = this.querySelector('[data-bhr-viewport]');
      this.allSlides = [...this.querySelectorAll('[data-bhr-slide]')];
      this.allDots = [...this.querySelectorAll('[data-bhr-dot]')];
      this.counter = this.querySelector('[data-bhr-current]');
      this.isSlide = this.classList.contains('bhr--slide');
      this.autoplay = this.dataset.autoplay === 'true' && !REDUCED.matches;
      this.duration = Math.max(2, Number(this.dataset.speed) || 5) * 1000;
      this.loop = this.dataset.loop !== 'false';
      this.current = 0;
      this.remaining = this.duration;
      this.pauses = new Set();

      this.style.setProperty('--bhr-duration', `${this.duration}ms`);

      this.onPrev = () => this.go(this.current - 1, true);
      this.onNext = () => this.go(this.current + 1, true);
      this.onClick = this.onClick.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onMedia = () => this.refresh();
      this.onVisibility = () => this.setPause('hidden', document.hidden);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      DESKTOP.addEventListener('change', this.onMedia);
      document.addEventListener('visibilitychange', this.onVisibility);

      if (this.dataset.pauseHover === 'true') {
        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.setPause('hover', true);
        });
        this.addEventListener('pointerleave', () => this.setPause('hover', false));
      }
      this.addEventListener('focusin', () => this.setPause('focus', true));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.setPause('focus', false);
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => this.setPause('offscreen', !entry.isIntersecting), {
          threshold: 0.2,
        });
        this.observer.observe(this);
      }

      this.setupDrag();
      this.setupEditor();
      this.refresh();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      DESKTOP.removeEventListener('change', this.onMedia);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    /* ---------- Slides visíveis neste tamanho de tela ---------- */

    get slides() {
      return this.allSlides.filter((slide) => getComputedStyle(slide).display !== 'none');
    }

    refresh() {
      const visible = this.slides;
      this.allDots.forEach((dot) => {
        const slide = this.allSlides[Number(dot.dataset.bhrDot)];
        dot.hidden = !visible.includes(slide);
      });

      const active = this.allSlides.find((slide) => slide.classList.contains('is-active'));
      const index = Math.max(0, visible.indexOf(active));
      this.go(index, false, true);
    }

    /* ---------- Troca ---------- */

    go(target, byUser = false, instant = false) {
      const slides = this.slides;
      const count = slides.length;
      if (!count) return;

      let index = target;
      if (index < 0) index = this.loop ? count - 1 : 0;
      if (index >= count) index = this.loop ? 0 : count - 1;
      this.current = index;

      const activeSlide = slides[index];
      this.allSlides.forEach((slide) => {
        const active = slide === activeSlide;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        slide.querySelectorAll('video').forEach((video) => {
          if (active) video.play?.().catch(() => {});
          else video.pause?.();
        });
      });

      if (this.isSlide && this.track) {
        if (instant) this.track.style.transition = 'none';
        this.track.style.transform = `translateX(${-index * 100}%)`;
        if (instant) {
          this.track.getBoundingClientRect();
          this.track.style.transition = '';
        }
      }

      const realIndex = this.allSlides.indexOf(activeSlide);
      this.allDots.forEach((dot) => {
        const active = Number(dot.dataset.bhrDot) === realIndex;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = String(index + 1).padStart(2, '0');

      // Reinicia a barra de tempo do traço ativo.
      const fill = this.querySelector('.bhr__dot.is-active .bhr__dot-fill');
      if (fill) {
        fill.style.animation = 'none';
        fill.getBoundingClientRect();
        fill.style.animation = '';
      }

      this.remaining = this.duration;
      this.schedule();
      if (byUser) this.dispatchEvent(new CustomEvent('bracci-hero:change', { detail: { index } }));
    }

    /* ---------- Reprodução automática ---------- */

    schedule() {
      clearTimeout(this.timer);
      if (!this.autoplay || this.slides.length < 2 || this.pauses.size > 0) return;

      this.startedAt = performance.now();
      this.timer = setTimeout(() => this.go(this.current + 1), this.remaining);
    }

    setPause(reason, paused) {
      if (!this.autoplay) return;
      const wasPaused = this.pauses.size > 0;
      if (paused) this.pauses.add(reason);
      else this.pauses.delete(reason);
      const isPaused = this.pauses.size > 0;

      this.classList.toggle('is-paused', isPaused);
      this.classList.toggle('is-stopped', this.pauses.has('button') || this.pauses.has('editor'));

      if (isPaused && !wasPaused) {
        clearTimeout(this.timer);
        this.remaining = Math.max(0, this.remaining - (performance.now() - (this.startedAt || performance.now())));
      } else if (!isPaused && wasPaused) {
        if (this.remaining < 300) this.remaining = 300;
        this.schedule();
      }
    }

    /* ---------- Cliques e teclado ---------- */

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (this.dragged) {
        event.preventDefault();
        event.stopPropagation();
        this.dragged = false;
        return;
      }

      if (target.closest('[data-bhr-prev]')) return this.onPrev();
      if (target.closest('[data-bhr-next]')) return this.onNext();

      const dot = target.closest('[data-bhr-dot]');
      if (dot) {
        const slide = this.allSlides[Number(dot.dataset.bhrDot)];
        return this.go(this.slides.indexOf(slide), true);
      }

      const pause = target.closest('[data-bhr-pause]');
      if (pause) {
        const stop = !this.pauses.has('button');
        this.setPause('button', stop);
        pause.setAttribute('aria-pressed', String(stop));
        pause.setAttribute('aria-label', stop ? 'Retomar banners' : 'Pausar banners');
        return;
      }

      if (target.closest('[data-bhr-scroll]')) this.scrollToNext();
    }

    onKey(event) {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.onPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.onNext();
      }
    }

    scrollToNext() {
      const section = this.closest('.shopify-section') || this;
      let next = section.nextElementSibling;
      while (next && next.offsetHeight === 0) next = next.nextElementSibling;
      if (!next) return;

      // O header fica grudado no topo: a seção para logo abaixo dele.
      const header = getComputedStyle(document.documentElement).getPropertyValue('--bh-header-bottom').trim();
      next.style.scrollMarginTop = header || '0px';
      next.scrollIntoView({ behavior: REDUCED.matches ? 'auto' : 'smooth', block: 'start' });
    }

    /* ---------- Arrasto ---------- */

    setupDrag() {
      const area = this.viewport;
      if (!area) return;

      let startX = 0;
      let startY = 0;
      let deltaX = 0;
      let tracking = false;
      let horizontal = null;

      area.addEventListener('pointerdown', (event) => {
        if (this.slides.length < 2 || (event.pointerType === 'mouse' && event.button !== 0)) return;
        tracking = true;
        horizontal = null;
        startX = event.clientX;
        startY = event.clientY;
        deltaX = 0;
        this.dragged = false;
      });

      area.addEventListener('pointermove', (event) => {
        if (!tracking) return;
        deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;

        if (horizontal === null && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
          horizontal = Math.abs(deltaX) > Math.abs(deltaY);
          if (horizontal) {
            area.setPointerCapture?.(event.pointerId);
            this.setPause('drag', true);
            if (this.isSlide) this.classList.add('is-dragging');
          }
        }
        if (!horizontal) return;

        if (this.isSlide && this.track) {
          const width = area.clientWidth || 1;
          this.track.style.transform = `translateX(calc(${-this.current * 100}% + ${deltaX}px))`;
          if (Math.abs(deltaX) > width) deltaX = Math.sign(deltaX) * width;
        }
      });

      const end = () => {
        if (!tracking) return;
        tracking = false;
        this.classList.remove('is-dragging');

        if (horizontal) {
          this.dragged = Math.abs(deltaX) > 10;
          const threshold = Math.min(80, (area.clientWidth || 300) * 0.12);
          if (deltaX <= -threshold) this.go(this.current + 1, true);
          else if (deltaX >= threshold) this.go(this.current - 1, true);
          else this.go(this.current, false);
          // O clique que vem logo após o arrasto é descartado em onClick.
          setTimeout(() => (this.dragged = false), 50);
        }
        this.setPause('drag', false);
        horizontal = null;
      };

      area.addEventListener('pointerup', end);
      area.addEventListener('pointercancel', end);
      area.addEventListener('lostpointercapture', end);
      area.addEventListener('dragstart', (event) => event.preventDefault());
    }

    /* ---------- Editor de temas ---------- */

    setupEditor() {
      if (!window.Shopify?.designMode) return;

      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest?.('[data-bhr-slide]');
        if (!slide) return;
        this.setPause('editor', true);
        const index = this.slides.indexOf(slide);
        if (index >= 0) this.go(index, false, true);
      });

      this.addEventListener('shopify:block:deselect', () => this.setPause('editor', false));
    }
  }

  customElements.define('bracci-hero', BracciHero);
})();
